import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../utils/supabaseClient';
import Modal from '../../components/Modal';

function highlightMatch(text, searchTerm) {
  if (!searchTerm) return text;
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? <mark key={i}>{part}</mark> : part
  );
}

function ViewWebsites() {
  const [websites, setWebsites] = useState([]);
  const [filteredWebsites, setFilteredWebsites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [form, setForm] = useState({
    domain: '',
    username: '',
    password: '',
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState(null);

  const searchInputRef = useRef(null);

  useEffect(() => {
    const fetchWebsites = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;

      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('websites')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching websites:', error);
      }

      setWebsites(data || []);
      setFilteredWebsites(data || []);
      setLoading(false);
    };

    fetchWebsites();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredWebsites(websites);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredWebsites(
        websites.filter(
          (w) =>
            w.domain?.toLowerCase().includes(term) ||
            w.username?.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, websites]);

  const openEditModal = (website) => {
    setSelectedWebsite(website);
    setForm(website);
    setEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleUpdateWebsite = async () => {
    if (!selectedWebsite?.id) {
      alert('Missing website ID.');
      return;
    }

    const updatePayload = {
      domain: form.domain,
      username: form.username,
      app_password: form.password,
      shop_name: form.shop_name || null,
    };

    const { error } = await supabase
      .from('websites')
      .update(updatePayload)
      .eq('id', selectedWebsite.id);

    if (error) {
      console.error('Error updating website:', error);
      alert('Failed to update website.');
      return;
    }

    const updatedWebsites = websites.map((w) =>
      w.id === selectedWebsite.id ? { ...w, ...updatePayload } : w
    );
    setWebsites(updatedWebsites);
    setFilteredWebsites(updatedWebsites);
    setEditModalOpen(false);
  };

  const confirmDeleteWebsite = (website) => {
    setWebsiteToDelete(website);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!websiteToDelete?.id) return;

    const { error } = await supabase
      .from('websites')
      .delete()
      .eq('id', websiteToDelete.id);

    if (error) {
      console.error('Error deleting website:', error);
      alert('Failed to delete website.');
      return;
    }

    setWebsites((prev) => prev.filter((w) => w.id !== websiteToDelete.id));
    setFilteredWebsites((prev) => prev.filter((w) => w.id !== websiteToDelete.id));
    setShowDeleteModal(false);
  };

  if (loading) {
    return (
      <div className="centered-loading">
        <p>Loading websites...</p>
      </div>
    );
  }

  return (
    <>
      <header>
        <div className="section-title">
          <h1>Websites</h1>
        </div>
        <div className="breadcrumbs">
          <p>Websites <i className="bi bi-chevron-right"></i> View All</p>
        </div>
      </header>

      <section className="scroll">
        <div className="contain">
          {websites.length > 0 && (
            <>
              <div className="view-search">
                <input
                  type="text"
                  id="view-search"
                  ref={searchInputRef}
                  placeholder="Search websites"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  className="btn-unset"
                  type="button"
                  onClick={() => searchInputRef.current?.focus()}
                >
                  <i className="bi bi-search"></i>
                </button>
              </div>
              {searchTerm.trim() && filteredWebsites.length === 0 && (
                <p className="no-results-message">
                  No results found for "<strong>{searchTerm}</strong>"
                </p>
              )}
            </>
          )}

          <div className="view-rows">
            {filteredWebsites.length === 0 && !searchTerm.trim() ? (
              <div className="no-content centered-loading">
                <button
                  type="button"
                  className="btn-outlined"
                  onClick={() => window.dispatchEvent(new Event('open-connect-modal'))}
                >
                  Connect your first website
                </button>
              </div>
            ) : (
              filteredWebsites.map((website) => (
                <div className="view-row profile-row" key={website.id}>
                  <div>
                    <h2>{highlightMatch(website.domain, searchTerm)}</h2>
                    {website.cms_type === 'wordpress' && (
                      <p><span>Type: {website.cms_type}</span> <span>User Name: {highlightMatch(website.username, searchTerm)}</span></p>
                    )}
                    {website.cms_type === 'shopify' && (
                      <p><span>Type: {website.cms_type}</span> <span>Shop Name: {highlightMatch(website.shop_name, searchTerm)}</span></p>
                    )}
                  </div>
                  <div className="view-row-actions">
                    <button
                      className="btn-outlined btn-delete"
                      onClick={() => confirmDeleteWebsite(website)}
                    >
                      Delete
                    </button>
                    <button
                      className="btn-outlined"
                      onClick={() => openEditModal(website)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {editModalOpen && (
        <Modal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          title="Edit Website"
          actions={[
            {
              label: 'Update',
              className: 'btn-outlined',
              onClick: handleUpdateWebsite,
            },
            {
              label: 'Cancel',
              className: 'btn-outlined',
              onClick: () => setEditModalOpen(false),
            },
          ]}
        >
          <form className="create-item">
            <label>Domain:</label>
            <input
              type="text"
              name="domain"
              value={form.domain}
              onChange={handleEditChange}
            />

            {selectedWebsite?.cms_type === 'wordpress' && (
              <>
                <label>Username:</label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleEditChange}
                />
                <label className="mb-0">Application Password:</label>
                <p className="label-note">Generated in WordPress: Users <i className="bi bi-chevron-right"></i> Profile</p>
                <input
                  type="text"
                  name="password"
                  value={form.password}
                  onChange={handleEditChange}
                />
              </>
            )}

            {selectedWebsite?.cms_type === 'shopify' && (
              <>
                <label>Shopify Store Name:</label>
                <input
                  type="text"
                  name="shop_name"
                  value={form.shop_name || ''}
                  onChange={handleEditChange}
                />
                <label className="mb-0">Private Admin API Token:</label>
                <p className="label-note">Log into your Shopify Admin to generate a Private Admin API token.</p>
                <input
                  type="text"
                  name="password"
                  value={form.password}
                  onChange={handleEditChange}
                />
              </>
            )}
          </form>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Confirm Delete"
          actions={[
            {
              label: 'Delete',
              className: 'btn-outlined btn-delete',
              onClick: handleDelete,
            },
            {
              label: 'Cancel',
              className: 'btn-outlined',
              onClick: () => setShowDeleteModal(false),
            },
          ]}
        >
          <p>
            Are you sure you want to delete{' '}
            <strong>{websiteToDelete?.domain}</strong>? This action cannot be undone.
          </p>
        </Modal>
      )}
    </>
  );
}

export default ViewWebsites;
